import type { BoxProps, TextProps } from '@chakra-ui/react';
import { Flex, Text, useStyleConfig, VisuallyHidden } from '@chakra-ui/react';
import { FormatPrice } from 'components/localisation/FormatPrice';

interface PriceProps extends BoxProps {
  colorScheme?: 'white' | 'gray';
  price: number;
  compareAtPrice?: number;
  availableForSale: boolean;
  beforeText?: string;
  afterText?: string;
  textProps?: TextProps;
  showPrice?: boolean;
}

export function ProductLineItemPrice({
  colorScheme = 'gray',
  price,
  compareAtPrice,
  availableForSale,
  beforeText,
  afterText,
  textProps = { size: 'title2' },
  showPrice = true,
  ...props
}: PriceProps) {
  const isWhite = colorScheme === 'white';
  const styles = useStyleConfig('Text', { size: 'detail' });
  const hasDiscount = compareAtPrice && compareAtPrice > price;

  return (
    <Flex
      ml="auto"
      justifyContent="flex-end"
      gap="4xs"
      sx={{
        '.decimal': { ...styles, fontWeight: 'medium' },
      }}
      {...props}
    >
      {!availableForSale && (
        <Text
          as="p"
          color={isWhite ? 'gray.50' : 'gray.600'}
          opacity={isWhite ? 0.6 : 0.36}
          {...textProps}
        >
          Sold out
        </Text>
      )}

      {showPrice && availableForSale && (
        <>
          {beforeText && (
            <Text color={isWhite ? 'gray.50' : 'gray.600'} {...textProps}>
              {beforeText}
            </Text>
          )}
          <VisuallyHidden>Now </VisuallyHidden>
          <FormatPrice
            className="price--now"
            color={isWhite ? 'gray.50' : 'gray.600'}
            amount={price}
            afterText={afterText}
            showFreeIfZero
            {...textProps}
          />
          {hasDiscount && (
            <>
              <VisuallyHidden>Was </VisuallyHidden>
              <FormatPrice
                className="price--was"
                color={isWhite ? 'gray.50' : 'gray.600'}
                opacity={isWhite ? 0.6 : 0.36}
                hasStrikeThrough
                amount={compareAtPrice}
                {...textProps}
              />
            </>
          )}
        </>
      )}
    </Flex>
  );
}
